import React from 'react';
import { MD_CAT_BLACK, MD_CAT_YELLOW } from './constants';
import { escHtml } from './textUtils';
import { UiMappingResponse } from './types';

// Wraps the already-rendered analysis HTML in a Caterpillar-styled report layout
// that html2canvas rasterizes into the downloaded PDF.
export const buildAnalysisReportHtml = (bodyHtml: string, generatedAt: string): string => `
<style>
  .rpt * { box-sizing: border-box; }
  .rpt { font-family: 'Noto Sans', Arial, sans-serif; color: #1A1A1A; background: #ffffff; padding: 28px 32px; width: 1060px; }
  .rpt .doc-header { border-bottom: 3px solid #FFCD11; padding-bottom: 14px; margin-bottom: 22px; }
  .rpt .doc-title { font-size: 22px; font-weight: 800; margin: 0; }
  .rpt .doc-sub { font-size: 12px; color: #666; margin-top: 4px; }
  .rpt table { border-collapse: collapse; width: 100%; font-size: 11px; word-break: break-word; }
  .rpt th, .rpt td { border: 1px solid #ececec; padding: 8px 10px; text-align: left; vertical-align: top; }
  .rpt thead th { background: #FFCD11; color: #1A1A1A; font-weight: 700; }
  .rpt tbody tr:nth-child(even) { background: #fafafa; }
  .rpt h1, .rpt h2, .rpt h3 { color: #1A1A1A; }
  .rpt h2 { border-bottom: 2px solid #FFCD11; padding-bottom: 4px; }
  .rpt a { color: #1565c0; }
  .rpt .rcb-row-actions { display: none !important; }
  .rpt .doc-footer { margin-top: 26px; padding-top: 10px; border-top: 1px solid #e0e0e0; font-size: 10px; color: #9a9a9a; }
</style>
<div class="rpt">
  <div class="doc-header">
    <h1 class="doc-title">Epic Coverage Analysis</h1>
    <div class="doc-sub">Generated ${generatedAt} &middot; Service Adoption Assistant</div>
  </div>
  ${bodyHtml}
  <div class="doc-footer">Responses may be inaccurate — please verify important information.</div>
</div>`;

// Shared pill style for the analysis action buttons (Export PDF / Comment in Epic).
export const analysisBtnStyle = (busy: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  borderRadius: '22px',
  border: `1.5px solid ${MD_CAT_BLACK}`,
  background: busy ? '#f0f0f0' : MD_CAT_YELLOW,
  color: MD_CAT_BLACK,
  fontWeight: 600,
  fontSize: '13px',
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.6 : 1,
});

// Replaces editable Notes textareas in a cloned analysis node with their current
// text value, so exported/posted output shows plain notes instead of form controls.
// `source` is the live node the clone came from (textarea values live on the DOM
// element, not in serialized innerHTML), matched to the clone by document order.
export const replaceNotesTextareas = (clone: HTMLElement, source: HTMLElement) => {
  const cloneAreas = Array.from(clone.querySelectorAll('textarea'));
  const sourceAreas = Array.from(source.querySelectorAll('textarea'));
  cloneAreas.forEach((ta, i) => {
    const value = sourceAreas[i]?.value ?? ta.value ?? ta.textContent ?? '';
    const span = clone.ownerDocument.createElement('span');
    span.textContent = value;
    ta.replaceWith(span);
  });
};

// Builds sanitized HTML for the epic comment from the rendered analysis (drops the
// per-row action buttons, which don't belong in the posted comment).
export const buildCommentHtml = (node: HTMLElement): string => {
  const clone = node.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.rcb-row-actions').forEach(el => el.remove());
  replaceNotesTextareas(clone, node);
  return `<div><h3>Epic Coverage Analysis</h3>${clone.innerHTML}<hr/><p><em>Posted by the Service Adoption Assistant.</em></p></div>`;
};

// Builds a beautified, Caterpillar-styled HTML report of a UI→API mapping for
// posting into an epic (inline styles so Azure DevOps keeps the formatting).
export const buildUiMappingCommentHtml = (capability: string, res: UiMappingResponse): string => {
  const mappings = res.mappings ?? [];
  const font = "font-family:'Segoe UI',Arial,sans-serif";
  const th = (h: string) =>
    `<th style="border:1px solid #ececec;padding:8px 10px;text-align:left;background:#FFCD11;color:#1A1A1A;font-weight:700;font-size:12px;${font}">${h}</th>`;
  const td = (v: string | null | undefined) =>
    `<td style="border:1px solid #ececec;padding:8px 10px;vertical-align:top;font-size:12px;color:#1A1A1A;${font}">${escHtml(v) || '—'}</td>`;
  const headers = ['UI Label', 'UI Type', 'Widget', 'Body', 'Mapped Field', 'Confidence', 'Notes'].map(th).join('');
  const rows = mappings
    .map((m, i) => `<tr style="background:${i % 2 ? '#fafafa' : '#ffffff'}">${td(m.ui_label)}${td(m.ui_type)}${td(m.ui_widget)}${td(m.body_type)}${td(m.mapped_field)}${td(m.confidence)}${td(m.notes)}</tr>`)
    .join('');
  const table = mappings.length
    ? `<table style="border-collapse:collapse;width:100%;${font}"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`
    : `<p style="font-size:13px;color:#888;${font}"><em>No UI elements could be mapped for this screenshot.</em></p>`;
  const apiChip = res.api_name
    ? `<span style="display:inline-block;background:#1A1A1A;color:#fff;border-radius:999px;padding:2px 10px;font-size:11px;font-weight:700;${font}">${escHtml(res.api_name)}</span>`
    : '';
  const countChip = mappings.length
    ? `<span style="display:inline-block;background:#e8f5e9;color:#2e7d32;border:1px solid #a9d9ac;border-radius:999px;padding:2px 10px;font-size:11px;font-weight:700;${font}">${mappings.length} field${mappings.length === 1 ? '' : 's'} mapped</span>`
    : '';
  return `<div style="${font};color:#1A1A1A">`
    + `<div style="border-bottom:3px solid #FFCD11;padding-bottom:10px;margin-bottom:14px">`
    +   `<div style="font-size:18px;font-weight:800;margin:0">UI → API Mapping</div>`
    +   `<div style="font-size:13px;color:#333;margin-top:6px"><strong>Capability:</strong> ${escHtml(capability)}</div>`
    +   `<div style="margin-top:8px">${apiChip}${apiChip && countChip ? '&nbsp;&nbsp;' : ''}${countChip}</div>`
    + `</div>`
    + table
    + `<div style="margin-top:18px;padding-top:8px;border-top:1px solid #e0e0e0;font-size:10px;color:#9a9a9a;${font}">Posted by the Service Adoption Assistant &middot; Generated ${escHtml(new Date().toLocaleString())} &middot; Responses may be inaccurate — please verify important information.</div>`
    + `</div>`;
};
